import { Component, Event, EventEmitter, Host, Method, Prop, State, Watch, h } from '@stencil/core';
import { eventPromise } from '../../utils/eventPromise';

/**
 * A basic unstyled accordion item component. To be used with `lg-accordion` components.
 *
 * @slot - The content of the accordion item.
 * @slot header - The header of the accordion item.
 */
@Component({
  tag: 'lg-accordion-item',
  styleUrl: 'lg-accordion-item.scss',
})
export class LgAccordionItem {
  elDetails: HTMLDetailsElement;
  elSummary: HTMLElement;
  elContent: HTMLElement;

  /**
   * Whether the accordion item is open or closed on load.
   */
  @Prop({ reflect: true, mutable: true }) open = false;

  @Watch('open')
  openChanged() {
    this.toggle(this.open);
  }

  @Event() lgAccordionToggle: EventEmitter<{ open: boolean }>;

  @State() isExpanded = false;

  @State() isExpanding = false;
  @State() isCollapsing = false;

  @State() fixedHeight?: number;

  @Method()
  async toggle(expand = !this.isExpanded): Promise<void> {
    if (expand) {
      return this.expand(false);
    } else {
      return this.contract(false);
    }
  }

  connectedCallback() {
    this.openChanged();
  }

  onSummaryClick(e: MouseEvent): void {
    e.preventDefault();

    if (this.elDetails.open) {
      this.contract();
    } else {
      this.expand();
    }
  }

  expand(emitEvent = true): void {
    if (this.isExpanded) {
      return;
    }

    if (!this.elSummary) {
      // component hasn't rendered yet
      // wait for next frame
      setTimeout(() => {
        this.expand(emitEvent);
      }, 0);

      return;
    }

    if (emitEvent) {
      this.lgAccordionToggle.emit({ open: true });
    }

    // fix current height
    this.fixedHeight = this.elSummary.offsetHeight;
    this.isExpanding = true;

    this.isExpanded = true;

    requestAnimationFrame(async () => {
      // set full height
      const fullHeight = this.elSummary.offsetHeight + this.elContent.offsetHeight;
      this.fixedHeight = fullHeight;

      // wait for transition to finish
      await eventPromise(this.elDetails, 'transitionend', 300, true);

      this.isExpanding = false;
      // make height dynamic again
      this.fixedHeight = undefined;
    });
  }

  contract(emitEvent = true): void {
    if (!this.isExpanded) {
      return;
    }

    if (emitEvent) {
      this.lgAccordionToggle.emit({ open: false });
    }

    // fix current height
    const fullHeight = this.elSummary.offsetHeight + this.elContent.offsetHeight;
    this.fixedHeight = fullHeight;
    this.isCollapsing = true;

    requestAnimationFrame(async () => {
      // set collapsed height
      this.fixedHeight = this.elSummary.offsetHeight;

      // wait for transition to finish
      await eventPromise(this.elDetails, 'transitionend', 300, true);

      this.isExpanded = false;
      this.isCollapsing = false;
      // make height dynamic again
      this.fixedHeight = undefined;
    });
  }

  render() {
    return (
      <Host
        class={{
          'lg-collapsing': this.isCollapsing,
          'lg-expanding': this.isExpanding,
          'lg-expanded': this.isExpanded,
          'lg-collapsed': !this.isExpanded,
        }}
      >
        <details open={this.isExpanded} style={{ height: this.fixedHeight ? `${this.fixedHeight}px` : null }} ref={el => (this.elDetails = el as HTMLDetailsElement)}>
          <summary ref={el => (this.elSummary = el)} onClick={event => this.onSummaryClick(event)}>
            <slot name="header"></slot>
          </summary>
          <div ref={el => (this.elContent = el)} class="content">
            <slot name="block"></slot>
          </div>
        </details>
      </Host>
    );
  }
}
